/**
 * Freigabe-Cockpit in the Bearbeitungsmaske: who asked to see this Eingang, how many, what
 * was decided. Button (open count), banner (names) and panel (decide via FreigabeEntscheid).
 * Fed by the participant view of useRequestsForComm.
 */

import { KeyRound } from 'lucide-react';
import { Badge } from '../../../components/ui/Badge';
import { cn } from '../../../lib/cn';
import { dateTimeShortDe } from '../../../lib/format';
import type { AccessRequestItem } from '../../../lib/api-client';
import { FreigabeEntscheid } from '../../communication-v2/components/FreigabeEntscheid';

const isOffen = (r: AccessRequestItem) => r.status === 'pending';

function nameOf(r: AccessRequestItem): string {
  return r.requester_name?.trim() ? r.requester_name : 'Ein Benutzer';
}

/** "Lara Meier und Marco Keller bitten" / "…, Marco Keller und 2 weitere bitten". */
export function bittenSatz(requests: AccessRequestItem[]): string {
  const names = [...new Set(requests.map(nameOf))];
  if (names.length === 1) return `${names[0]} bittet`;
  if (names.length === 2) return `${names[0]} und ${names[1]} bitten`;
  return `${names[0]}, ${names[1]} und ${names.length - 2} weitere bitten`;
}

export function FreigabeCockpitButton({
  requests,
  onOpen,
}: {
  requests: AccessRequestItem[];
  onOpen: () => void;
}) {
  const offen = requests.filter(isOffen).length;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Freigabe-Anfragen: ${offen} offen, ${requests.length} insgesamt`}
      className={cn(
        'flex shrink-0 items-center gap-1.5 rounded-md border px-2 py-1 text-xs',
        offen > 0
          ? 'border-status-warning-border bg-status-warning-bg text-status-warning'
          : 'border-border text-muted-foreground hover:bg-accent',
      )}
    >
      <KeyRound className="size-3.5" />
      Freigabe-Anfragen
      <span className="rounded-full bg-background/70 px-1.5 font-medium tabular-nums">
        {offen > 0 ? offen : requests.length}
      </span>
    </button>
  );
}

export function FreigabeCockpitBanner({
  requests,
  onOpen,
}: {
  requests: AccessRequestItem[];
  onOpen: () => void;
}) {
  const offen = requests.filter(isOffen);
  if (offen.length === 0) return null;
  return (
    <div
      role="status"
      className="mb-4 flex items-center gap-3 rounded-md border border-sem-decide/30 bg-sem-decide-bg/40 px-3 py-2 text-sm"
    >
      <KeyRound className="size-4 shrink-0 text-status-warning" />
      <p className="min-w-0 flex-1 text-foreground">
        {bittenSatz(offen)} um Freigabe dieses Eingangs.
      </p>
      <button
        type="button"
        onClick={onOpen}
        className="shrink-0 rounded px-2 py-1 text-xs font-medium text-foreground underline-offset-2 hover:underline"
      >
        Anfragen ansehen
      </button>
    </div>
  );
}

export function FreigabeCockpitPanel({
  requests,
  onFeedback,
  onError,
}: {
  requests: AccessRequestItem[];
  onFeedback?: (msg: string) => void;
  onError?: (msg: string) => void;
}) {
  const offen = requests.filter(isOffen);
  const entschieden = requests.filter((r) => !isOffen(r));
  const erteilt = entschieden.filter((r) => r.status === 'approved').length;
  const abgelehnt = entschieden.length - erteilt;

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-muted-foreground">
        {requests.length === 1 ? '1 Anfrage' : `${requests.length} Anfragen`} · {offen.length} offen ·{' '}
        {erteilt} erteilt · {abgelehnt} abgelehnt
      </p>

      <section aria-label="Offene Anfragen" className="flex flex-col gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Offen ({offen.length})
        </h3>
        {offen.length === 0 ? (
          <p className="text-sm text-muted-foreground">Keine offenen Anfragen.</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {offen.map((r) => (
              <li key={r.id} className="flex flex-col gap-2 rounded-md border border-border p-3 text-sm">
                <FreigabeEntscheid
                  request={r}
                  heading="requester"
                  onFeedback={onFeedback}
                  onError={onError}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      {entschieden.length > 0 && (
        <section aria-label="Entschiedene Anfragen" className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Entschieden ({entschieden.length})
          </h3>
          <ul className="flex flex-col gap-2">
            {entschieden.map((r) => (
              <li key={r.id} className="rounded-md border border-border p-3 text-sm">
                <div className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate font-medium text-foreground">{nameOf(r)}</span>
                  <Badge variant={r.status === 'approved' ? 'success' : 'neutral'}>
                    {r.status === 'approved' ? 'Erteilt' : 'Abgelehnt'}
                  </Badge>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  angefragt {dateTimeShortDe(r.created_at)}
                  {r.reviewed_at
                    ? ` · entschieden ${dateTimeShortDe(r.reviewed_at)}${
                        r.reviewer_name?.trim() ? ` von ${r.reviewer_name}` : ''
                      }`
                    : ''}
                </p>
                {r.reason?.trim() && (
                  <p className="mt-1 text-xs text-foreground">
                    <span className="text-muted-foreground">Begründung:</span> {r.reason}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
